// Lightweight, dependency-free CSV parsing for contact imports.
// Handles quoted fields, escaped quotes (""), commas inside quotes, CRLF and BOM.

export function parseCSV(text) {
  text = (text || "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ",") { row.push(field); field = ""; i++; continue; }
    if (ch === "\r") { i++; continue; }
    if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
    field += ch; i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}

const HEADER_MAP = {
  network: "network", lane: "network", group: "network",
  "company name": "company", company: "company", companyname: "company", "company_name": "company",
  country: "country",
  city: "city", town: "city",
  "contact name": "contact_name", contact: "contact_name", name: "contact_name",
  "contact person": "contact_name", "contact_name": "contact_name", "full name": "contact_name",
  email: "email", "e-mail": "email", "email address": "email", mail: "email",
  phone: "phone", tel: "phone", telephone: "phone", mobile: "phone", "phone number": "phone",
  role: "role", title: "role", position: "role", designation: "role", "job title": "role",
  website: "website", url: "website", web: "website", site: "website",
  services: "services", service: "services",
};

// Map a raw header to a canonical key. Tries an exact match first, then a FUZZY
// substring match so real-world variants ("E-Mail Address", "Email ID", "Contact Person
// Name", "Member Company", "Tel/Mobile", "Web site"…) still land in the right field.
function mapHeader(raw) {
  const h = (raw || "").trim().toLowerCase();
  if (!h) return null;
  if (HEADER_MAP[h]) return HEADER_MAP[h];
  const has = (...ws) => ws.some((w) => h.includes(w));
  // Order matters: check email + contact-name BEFORE the broad "company"/"name" rules.
  if (has("mail")) return "email";                                   // email, e-mail, email id…
  if (has("contact", "person", "representative", "member name", "full name")) return "contact_name";
  if (h === "name") return "contact_name";
  if (has("company", "agent", "firm", "organisation", "organization", "office", "member")) return "company";
  if (has("network", "lane", "segment", "group", "category")) return "network";
  if (has("country", "nation")) return "country";
  if (has("city", "town", "location")) return "city";
  if (has("phone", "tel", "mobile", "cell", "whats")) return "phone";
  if (has("title", "role", "position", "designation", "job")) return "role";
  if (has("web", "site", "url", "www")) return "website";
  if (has("service", "trade lane", "specialit", "speciality")) return "services";
  return null;
}

// Pull the first valid-looking email out of a cell that may hold several
// ("a@x.com; b@x.com" / "a@x.com / b@x.com" / "a@x.com, b@x.com").
function firstEmail(val) {
  const m = (val || "").match(/[^\s,;/<>()]+@[^\s,;/<>()]+\.[^\s,;/<>()]+/);
  return m ? m[0].trim() : (val || "").trim();
}

// Parse CSV text into normalized contact objects.
export function csvToContacts(text) {
  const rows = parseCSV(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => (h || "").trim());
  const keys = headers.map((h) => mapHeader(h));
  const out = [];
  for (let r = 1; r < rows.length; r++) {
    const obj = {};
    rows[r].forEach((val, idx) => {
      const k = keys[idx];
      if (!k) return;
      let v = (val || "").trim();
      if (k === "email") v = firstEmail(v).toLowerCase();
      // Don't overwrite an already-filled field if two columns map to the same key.
      if (v && !obj[k]) obj[k] = v;
    });
    if (Object.values(obj).some((v) => v)) out.push(obj);
  }
  return out;
}

export function countWithEmail(contacts) {
  return (contacts || []).filter((c) => c.email && c.email.includes("@")).length;
}
